'use client';

import { MotionConfig, motion, type Transition, type Variants } from 'framer-motion';

import { cn } from '@/lib/ui-kit/utils';

interface BurgerIconProps {
  isOpen: boolean;
  className?: string;
}

// ── Timings ──
// Lifted frame-by-frame from the reference recording (60fps): the bars fold into one,
// the whole glyph spins 270° while the outer bars scissor ±45° into an X; close reverses.
const EASE_OUT_CUBIC: Transition['ease'] = [0.33, 1, 0.68, 1];
const BAR_GAP = 6;
const SPIN_DELAY = 0.28;
const UNFOLD_DELAY = 0.62;

const glyphVariants: Variants = {
  open: { rotate: 270, transition: { delay: SPIN_DELAY, duration: 0.75, ease: EASE_OUT_CUBIC } },
  closed: { rotate: 0, transition: { duration: 0.52, ease: EASE_OUT_CUBIC } },
};

const middleBarVariants: Variants = {
  open: { opacity: 0, transition: { delay: SPIN_DELAY, duration: 0 } },
  closed: { opacity: 1, transition: { delay: UNFOLD_DELAY, duration: 0 } },
};

const outerBarVariants = (side: 1 | -1): Variants => ({
  open: {
    y: 0,
    rotate: side * 45,
    transition: {
      y: { duration: 0.1, ease: 'easeOut' },
      rotate: { delay: 0.37, duration: 0.27, ease: EASE_OUT_CUBIC },
    },
  },
  closed: {
    y: side * -BAR_GAP,
    rotate: 0,
    transition: {
      rotate: { duration: 0.2, ease: EASE_OUT_CUBIC },
      y: { delay: UNFOLD_DELAY, duration: 0.1, ease: 'easeOut' },
    },
  },
});

const topBarVariants = outerBarVariants(1);
const bottomBarVariants = outerBarVariants(-1);

/** Burger glyph that morphs into a close X while `isOpen`. */
export function BurgerIcon({ isOpen, className }: BurgerIconProps) {
  const state = isOpen ? 'open' : 'closed';

  // Every bar is drawn on the centre line and offset by `y`, so all transform origins sit at 12,12.
  return (
    <MotionConfig reducedMotion="user">
      <motion.svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden
        className={cn('size-6', className)}
        initial={false}
        animate={state}
      >
        <motion.g
          variants={glyphVariants}
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
        >
          <motion.line x1={4} y1={12} x2={20} y2={12} variants={topBarVariants} />
          <motion.line x1={4} y1={12} x2={20} y2={12} variants={middleBarVariants} />
          <motion.line x1={4} y1={12} x2={20} y2={12} variants={bottomBarVariants} />
        </motion.g>
      </motion.svg>
    </MotionConfig>
  );
}
